import React, { useState } from 'react';

import {
	Activity,
	Check,
	Clock,
	Copy,
	Eye,
	Key,
	ShieldCheck,
	Trash2,
} from 'lucide-react';

import {
	useCreateAPIKeyMutation,
	useListAPIKeysQuery,
	useRevealAPIKeyMutation,
	useRevokeAPIKeyMutation,
} from '../../api/apiKeys';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../providers/AppAuthProvider';
import {
	Alert,
	Button,
	ConfirmBox,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	SkeletonTable,
	StatusBadge,
	Typography,
} from '../common';

import type { APIKey } from '../../api/apiKeys';

interface APIKeysTabProps {
	enterpriseId?: string;
}

const APIKeysTab: React.FC<APIKeysTabProps> = ({ enterpriseId }) => {
	const { isAdmin, user } = useAuth();
	const { data, isLoading } = useListAPIKeysQuery(
		enterpriseId ? { enterpriseId: enterpriseId } : undefined,
	);
	const [createAPIKey, { isLoading: isCreating }] = useCreateAPIKeyMutation();
	const [revokeAPIKey, { isLoading: isRevoking }] = useRevokeAPIKeyMutation();
	const [revealAPIKey] = useRevealAPIKeyMutation();
	const { showToast } = useToast();

	const [showCreateModal, setShowCreateModal] = useState(false);
	const [newKeyName, setNewKeyName] = useState('');
	const [createdKey, setCreatedKey] = useState<APIKey | null>(null);
	const [revealedKey, setRevealedKey] = useState<{
		id: string;
		key: string;
	} | null>(null);
	const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const handleCreate = async () => {
		if (!newKeyName.trim()) {
			showToast('Error', 'Key name is required', 'error');
			return;
		}

		if (!enterpriseId) {
			showToast(
				'Error',
				'Organization ID is required to create a key',
				'error',
			);
			return;
		}

		try {
			const response = await createAPIKey({
				key_name: newKeyName,
				enterprise_id: enterpriseId,
			}).unwrap();
			setCreatedKey(response.api_key);
			showToast('Success', 'API key created successfully', 'success');
		} catch (error: any) {
			showToast(
				'Error',
				error?.data?.message || 'Failed to create API key',
				'error',
			);
		}
	};

	const handleRevoke = async (keyId: string) => {
		setRevokingKeyId(keyId);
	};

	const confirmRevoke = async () => {
		if (!revokingKeyId) return;
		try {
			await revokeAPIKey({ keyId: revokingKeyId, enterpriseId }).unwrap();
			showToast('Success', 'API key revoked', 'success');
		} catch (error: any) {
			showToast(
				'Error',
				error?.data?.message || 'Failed to revoke API key',
				'error',
			);
		} finally {
			setRevokingKeyId(null);
		}
	};

	const handleReveal = async (keyId: string) => {
		try {
			const response = await revealAPIKey({ keyId, enterpriseId }).unwrap();
			setRevealedKey({ id: keyId, key: response.full_key });
		} catch (error: any) {
			showToast(
				'Error',
				error?.data?.message || 'This key cannot be revealed.',
				'error',
			);
		}
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard
			.writeText(text)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch((err) => {
				console.error('Failed to copy text:', err);
				showToast('Error', 'Could not copy to clipboard.', 'error');
			});
	};

	const formatUsage = (count: number) => {
		return count.toLocaleString();
	};

	if (isLoading) {
		return <SkeletonTable rows={5} columns={5} />;
	}

	const apiKeys = data?.api_keys || [];

	return (
		<div className="w-full bg-white border border-gray-200 rounded-lg">
			<div className="flex items-center justify-between p-6 border-b border-gray-100">
				<div>
					<Typography variant="h4" className="text-gray-900">
						API Keys
					</Typography>
					<Typography variant="body2" className="text-gray-500">
						Manage machine-to-machine authentication keys for your tenant.
					</Typography>
				</div>
				{isAdmin && (
					<Button
						onClick={() => {
							setCreatedKey(null);
							setNewKeyName('');
							setShowCreateModal(true);
						}}
						variant="primary"
						icon={Key}
					>
						Create New Key
					</Button>
				)}
			</div>

			<div className="overflow-x-auto">
				<table className="min-w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
						<tr>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Name
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Prefix
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Usage (Today/Total)
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Last Used
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Status
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{apiKeys.map((key) => (
							<tr key={key.id} className="hover:bg-gray-50">
								<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
									<div className="flex items-center gap-2">
										<ShieldCheck className="w-4 h-4 text-primary-500" />
										{key.key_name}
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
									{key.key_prefix}...
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
									<div className="flex items-center gap-2">
										<Activity className="w-4 h-4 text-gray-400" />
										<span>{formatUsage(key.usage?.requests_today || 0)}</span>
										<span className="text-gray-300">/</span>
										<span className="text-xs">
											{formatUsage(key.usage?.total_requests || 0)}
										</span>
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
									<div className="flex items-center gap-1">
										<Clock className="w-4 h-4 text-gray-300" />
										{key.last_used_at
											? new Date(key.last_used_at).toLocaleDateString()
											: 'Never'}
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<StatusBadge status={key.is_active ? 'active' : 'inactive'}>
										{key.is_active ? 'Active' : 'Revoked'}
									</StatusBadge>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
									<div className="flex items-center justify-end gap-2">
										{isAdmin && key.is_active && (
											<Button
												onClick={() => handleReveal(key.id)}
												variant="ghost"
												size="xs"
												className="text-gray-500 hover:text-primary-600"
												icon={Eye}
												title="Reveal full key"
											/>
										)}
										{isAdmin && key.is_active && (
											<Button
												onClick={() => handleRevoke(key.id)}
												variant="outline"
												size="xs"
												className="text-red-600 hover:text-red-900 border-red-200 hover:bg-red-50"
												icon={Trash2}
											>
												Revoke
											</Button>
										)}
									</div>
								</td>
							</tr>
						))}
						{apiKeys.length === 0 && (
							<tr>
								<td colSpan={6} className="px-6 py-20 text-center">
									<div className="flex flex-col items-center justify-center">
										<Key className="w-16 h-16 text-gray-200 mb-4" />
										<Typography
											variant="body1"
											className="text-gray-900 font-medium"
										>
											No API keys found
										</Typography>
										<Typography
											variant="body2"
											className="text-gray-500 max-w-xs mx-auto mt-2"
										>
											{isAdmin
												? 'Create your first key to start using machine-to-machine authentication.'
												: 'No active API keys found for this organization.'}
										</Typography>
									</div>
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			<Modal
				isOpen={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				title={createdKey ? 'API Key Created' : 'Create New API Key'}
				size="md"
			>
				<ModalBody>
					{!createdKey ? (
						<div className="space-y-4">
							<Typography variant="body2" className="text-gray-600">
								Provide a descriptive name for your API key. This will help you
								identify its purpose later.
							</Typography>
							<div>
								<Typography
									variant="caption"
									className="mb-1 block font-medium"
								>
									Key Name
								</Typography>
								<Input
									value={newKeyName}
									onChange={(e) => setNewKeyName(e.target.value)}
									placeholder="e.g. CI/CD Integration, Marketing Service"
									fullWidth
									autoFocus
								/>
							</div>

							{user?.tenant_id && (
								<div>
									<Typography
										variant="caption"
										className="mb-1 block font-medium text-gray-700"
									>
										Your Tenant ID
									</Typography>
									<div className="bg-gray-50 p-2 rounded border border-gray-200 font-mono text-xs text-gray-600">
										{user.tenant_id}
									</div>
									<Typography
										variant="caption"
										className="text-gray-500 mt-1 block"
									>
										This ID must be included in the{' '}
										<code className="bg-gray-100 px-1 rounded text-[10px]">
											X-Tenant-ID
										</code>{' '}
										header.
									</Typography>
								</div>
							)}
							<Alert variant="info" title="Rate Limiting">
								Default rate limit is 1,000 requests per hour. You can update
								this later by contacting support.
							</Alert>
						</div>
					) : (
						<div className="space-y-4">
							<Alert variant="warning" title="Save this key now!">
								For security reasons, we only show this key once. If you lose
								it, you will need to create a new one.
							</Alert>
							<div
								data-ph-mask
								className="ph-no-capture bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm break-all relative group"
							>
								{createdKey.full_key}
								<button
									onClick={() => copyToClipboard(createdKey.full_key || '')}
									className="absolute right-2 top-2 p-2 bg-white rounded-md shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
									title="Copy to clipboard"
								>
									{copied ? (
										<Check className="w-4 h-4 text-green-500" />
									) : (
										<Copy className="w-4 h-4 text-gray-500" />
									)}
								</button>
							</div>

							{createdKey.tenant_id && (
								<div className="mt-2">
									<Typography
										variant="caption"
										className="mb-1 block font-medium text-gray-700"
									>
										Tenant ID (Required for header)
									</Typography>
									<div className="bg-gray-50 p-3 rounded-lg border border-gray-200 font-mono text-sm break-all flex items-center justify-between">
										<span>{createdKey.tenant_id}</span>
										<button
											onClick={() => {
												navigator.clipboard
													.writeText(createdKey.tenant_id || '')
													.then(() =>
														showToast('Success', 'Tenant ID copied', 'success'),
													)
													.catch((err) => {
														console.error('Failed to copy Tenant ID:', err);
														showToast(
															'Error',
															'Could not copy Tenant ID.',
															'error',
														);
													});
											}}
											className="p-1.5 bg-white rounded-md shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors ml-2"
											title="Copy Tenant ID"
										>
											<Copy className="w-3 h-3 text-gray-500" />
										</button>
									</div>
								</div>
							)}

							<Typography
								variant="caption"
								className="text-gray-500 text-center block italic"
							>
								Use this key in the{' '}
								<code className="bg-gray-100 px-1 rounded">X-API-Key</code>{' '}
								header of your requests.
							</Typography>
						</div>
					)}
				</ModalBody>
				<ModalFooter>
					{!createdKey ? (
						<>
							<Button
								onClick={() => setShowCreateModal(false)}
								variant="secondary"
							>
								Cancel
							</Button>
							<Button
								onClick={handleCreate}
								variant="primary"
								loading={isCreating}
							>
								Generate Key
							</Button>
						</>
					) : (
						<Button
							onClick={() => setShowCreateModal(false)}
							variant="primary"
							fullWidth
						>
							I've saved the key
						</Button>
					)}
				</ModalFooter>
			</Modal>

			<Modal
				isOpen={!!revealedKey}
				onClose={() => setRevealedKey(null)}
				title="API Key Revealed"
				size="md"
			>
				<ModalBody>
					<div className="space-y-4">
						<Alert variant="info" title="Full API Key">
							This is your full API key. Keep it secure as it will not be shown
							again once you close this modal.
						</Alert>
						<div
							data-ph-mask
							className="ph-no-capture bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm break-all relative group"
						>
							{revealedKey?.key}
							<button
								onClick={() => {
									navigator.clipboard
										.writeText(revealedKey?.key || '')
										.then(() =>
											showToast(
												'Success',
												'Key copied to clipboard',
												'success',
											),
										)
										.catch((err) => {
											console.error('Failed to copy key:', err);
											showToast('Error', 'Could not copy key.', 'error');
										});
								}}
								className="absolute right-2 top-2 p-2 bg-white rounded-md shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
								title="Copy to clipboard"
							>
								<Copy className="w-4 h-4 text-gray-500" />
							</button>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button
						onClick={() => setRevealedKey(null)}
						variant="primary"
						fullWidth
					>
						Done
					</Button>
				</ModalFooter>
			</Modal>

			{/* Revoke Confirmation Modal */}
			<ConfirmBox
				isOpen={!!revokingKeyId}
				onClose={() => setRevokingKeyId(null)}
				onConfirm={confirmRevoke}
				title="Revoke API Key"
				description="Are you sure you want to revoke this API key? This will immediately disable it and any applications using it will no longer be able to authenticate. This action cannot be undone."
				confirmText="Revoke Key"
				variant="danger"
				isLoading={isRevoking}
			/>
		</div>
	);
};

export default APIKeysTab;
